import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';

export type WebImageType =
  | 'collection'
  | 'delivery'
  | 'courtesy'
  | 'damage'
  | 'signature'
  | 'standard'
  | 'additional';

interface UploadImageInput {
  companyId: string;
  jobId: string;
  file: File;
  imageType: WebImageType;
}

interface ImageGpsLocation {
  latitude: number;
  longitude: number;
}

interface ImageExifMetadata {
  location: ImageGpsLocation | null;
  capturedAt: number | null;
}

interface PendingUploadUrlResponse {
  alreadyFinalized?: false;
  uploadUrl: string;
  storagePath: string;
  expiresAt: string;
  method: 'PUT';
  headers?: Record<string, string>;
}

interface FinalizedUploadUrlResponse {
  alreadyFinalized: true;
  status: 'finalized' | 'duplicate';
  storagePath: string;
  downloadURL?: string | null;
}

type UploadUrlResponse = PendingUploadUrlResponse | FinalizedUploadUrlResponse;

interface FinalizeResponse {
  success: boolean;
  downloadURL: string;
  storagePath: string;
  imageId: string;
  alreadyFinalized?: boolean;
}

const MAX_IMAGE_BYTES = 10 * 1024 * 1024;

export async function uploadJobImage({
  companyId,
  jobId,
  file,
  imageType,
}: UploadImageInput): Promise<FinalizeResponse> {
  const photoMetadata = await extractImageMetadata(file);
  const jpeg = await fileToJpegBlob(file);
  if (jpeg.size <= 0 || jpeg.size > MAX_IMAGE_BYTES) {
    throw new Error('Image must be a JPEG under 10MB after compression.');
  }

  const checksum = await sha256Hex(jpeg);
  // Match iOS SecureImageManager: one byte stream has one stable identity.
  // Retrying or selecting the same exact file cannot create a second record.
  const imageId = checksum.slice(0, 32);
  const payload = {
    companyId,
    jobId,
    imageId,
    imageType,
    checksum,
    byteSize: jpeg.size,
    contentType: 'image/jpeg',
    // Evidence coordinates describe the photograph, not the job. Never fill
    // missing EXIF from a pickup/job position: that would turn an operational
    // location into a false claim about where the image was captured.
    latitude: photoMetadata.location?.latitude ?? null,
    longitude: photoMetadata.location?.longitude ?? null,
    capturedAt: photoMetadata.capturedAt,
  };

  const createUrl = httpsCallable<typeof payload, UploadUrlResponse>(
    functions,
    'createImageUploadUrl'
  );
  const signed = await createUrl(payload);

  // Content-addressed retries can arrive after the storage trigger or another
  // client has already finalized the identical bytes. That is success, not a
  // reason to PUT again or leave the page in an upload-error state.
  if (signed.data.alreadyFinalized) {
    return {
      success: true,
      downloadURL: signed.data.downloadURL ?? '',
      storagePath: signed.data.storagePath,
      imageId,
      alreadyFinalized: true,
    };
  }

  const uploadResponse = await fetch(signed.data.uploadUrl, {
    method: signed.data.method || 'PUT',
    headers: signed.data.headers ?? { 'Content-Type': 'image/jpeg' },
    body: jpeg,
  });
  // The signed URL is create-only. HTTP 412 means this pending image already
  // reached Storage during an earlier attempt; finalize verifies its checksum
  // and either accepts the exact bytes or rejects the evidence.
  if (!uploadResponse.ok && uploadResponse.status !== 412) {
    throw new Error(`Image upload failed with HTTP ${uploadResponse.status}.`);
  }

  const finalize = httpsCallable<typeof payload, FinalizeResponse>(
    functions,
    'finalizeImageUpload'
  );
  const finalized = await finalize(payload);
  return finalized.data;
}

async function extractImageMetadata(file: File): Promise<ImageExifMetadata> {
  const empty: ImageExifMetadata = { location: null, capturedAt: null };
  if (!/jpe?g$/i.test(file.type) && !/\.(jpe?g)$/i.test(file.name)) {
    return empty;
  }

  try {
    const buffer = await file.arrayBuffer();
    const view = new DataView(buffer);
    if (view.byteLength < 4 || view.getUint16(0, false) !== 0xffd8) return empty;

    let offset = 2;
    while (offset + 4 <= view.byteLength) {
      if (view.getUint8(offset) !== 0xff) break;
      const marker = view.getUint8(offset + 1);
      const length = view.getUint16(offset + 2, false);
      if (length < 2 || offset + 2 + length > view.byteLength) break;

      if (marker === 0xe1 && hasExifHeader(view, offset + 4)) {
        return parseExifMetadata(view, offset + 10, offset + 2 + length);
      }

      offset += 2 + length;
    }
  } catch {
    return empty;
  }

  return empty;
}

function hasExifHeader(view: DataView, offset: number) {
  return (
    offset + 6 <= view.byteLength &&
    view.getUint8(offset) === 0x45 &&
    view.getUint8(offset + 1) === 0x78 &&
    view.getUint8(offset + 2) === 0x69 &&
    view.getUint8(offset + 3) === 0x66 &&
    view.getUint8(offset + 4) === 0x00 &&
    view.getUint8(offset + 5) === 0x00
  );
}

function parseExifMetadata(
  view: DataView,
  tiffStart: number,
  segmentEnd: number
): ImageExifMetadata {
  const empty: ImageExifMetadata = { location: null, capturedAt: null };
  if (tiffStart + 8 > segmentEnd) return empty;
  const byteOrder = view.getUint16(tiffStart, false);
  const littleEndian = byteOrder === 0x4949;
  if (!littleEndian && byteOrder !== 0x4d4d) return empty;
  if (view.getUint16(tiffStart + 2, littleEndian) !== 42) return empty;

  const ifd0Offset = view.getUint32(tiffStart + 4, littleEndian);
  const ifd0 = readIfdEntries(
    view,
    tiffStart,
    segmentEnd,
    tiffStart + ifd0Offset,
    littleEndian
  );

  let location: ImageGpsLocation | null = null;
  const gpsInfoOffset = ifd0.get(0x8825)?.valueOffset;
  if (gpsInfoOffset != null) {
    const gpsIfd = readIfdEntries(
      view,
      tiffStart,
      segmentEnd,
      tiffStart + gpsInfoOffset,
      littleEndian
    );
    const latRef = readAsciiValue(
      view,
      tiffStart,
      segmentEnd,
      gpsIfd.get(0x0001),
      littleEndian
    );
    const lat = readRationalTriplet(
      view,
      tiffStart,
      segmentEnd,
      gpsIfd.get(0x0002),
      littleEndian
    );
    const lonRef = readAsciiValue(
      view,
      tiffStart,
      segmentEnd,
      gpsIfd.get(0x0003),
      littleEndian
    );
    const lon = readRationalTriplet(
      view,
      tiffStart,
      segmentEnd,
      gpsIfd.get(0x0004),
      littleEndian
    );

    if (lat != null && lon != null && /^[NS]$/i.test(latRef ?? '') && /^[EW]$/i.test(lonRef ?? '')) {
      const latitude = latRef?.toUpperCase().startsWith('S') ? -lat : lat;
      const longitude = lonRef?.toUpperCase().startsWith('W') ? -lon : lon;
      if (
        Number.isFinite(latitude) &&
        Number.isFinite(longitude) &&
        latitude >= -90 &&
        latitude <= 90 &&
        longitude >= -180 &&
        longitude <= 180 &&
        !(latitude === 0 && longitude === 0)
      ) {
        location = { latitude, longitude };
      }
    }
  }

  const exifInfoOffset = ifd0.get(0x8769)?.valueOffset;
  const exifIfd =
    exifInfoOffset == null
      ? new Map<number, IfdEntry>()
      : readIfdEntries(
          view,
          tiffStart,
          segmentEnd,
          tiffStart + exifInfoOffset,
          littleEndian
        );
  const originalDate = readAsciiValue(view, tiffStart, segmentEnd, exifIfd.get(0x9003), littleEndian);
  const capturedAt = parseExifDateTime(
    originalDate ?? readAsciiValue(view, tiffStart, segmentEnd, ifd0.get(0x0132), littleEndian),
    readAsciiValue(view, tiffStart, segmentEnd, exifIfd.get(originalDate ? 0x9011 : 0x9010), littleEndian)
  );

  return { location, capturedAt };
}

interface IfdEntry {
  type: number;
  count: number;
  valueOffset: number;
  valueBytes: number[];
}

function readIfdEntries(
  view: DataView,
  tiffStart: number,
  segmentEnd: number,
  ifdOffset: number,
  littleEndian: boolean
) {
  const entries = new Map<number, IfdEntry>();
  if (ifdOffset + 2 > segmentEnd) return entries;
  const count = view.getUint16(ifdOffset, littleEndian);
  for (let index = 0; index < count; index += 1) {
    const entryOffset = ifdOffset + 2 + index * 12;
    if (entryOffset + 12 > segmentEnd) break;
    const tag = view.getUint16(entryOffset, littleEndian);
    entries.set(tag, {
      type: view.getUint16(entryOffset + 2, littleEndian),
      count: view.getUint32(entryOffset + 4, littleEndian),
      valueOffset: view.getUint32(entryOffset + 8, littleEndian),
      valueBytes: [
        view.getUint8(entryOffset + 8),
        view.getUint8(entryOffset + 9),
        view.getUint8(entryOffset + 10),
        view.getUint8(entryOffset + 11),
      ],
    });
  }
  void tiffStart;
  return entries;
}

function readAsciiValue(
  view: DataView,
  tiffStart: number,
  segmentEnd: number,
  entry: IfdEntry | undefined,
  littleEndian: boolean
) {
  if (!entry || entry.type !== 2 || entry.count < 1) return null;
  if (entry.count <= 4) {
    const bytes = littleEndian ? entry.valueBytes : entry.valueBytes;
    return String.fromCharCode(...bytes.slice(0, entry.count).filter((code) => code !== 0));
  }
  const valueOffset = tiffStart + entry.valueOffset;
  if (valueOffset < 0 || valueOffset + entry.count > segmentEnd) return null;
  let value = '';
  for (let index = 0; index < entry.count; index += 1) {
    const code = view.getUint8(valueOffset + index);
    if (code === 0) break;
    value += String.fromCharCode(code);
  }
  return value;
}

function parseExifDateTime(value: string | null, utcOffset: string | null): number | null {
  if (!value) return null;
  const match = value.trim().match(
    /^(\d{4}):(\d{2}):(\d{2})[ T](\d{2}):(\d{2}):(\d{2})$/
  );
  if (!match) return null;
  const [, year, month, day, hour, minute, second] = match.map(Number);
  if (year < 2020 || year >= 2100) return null;
  const calendarDate = new Date(Date.UTC(year, month - 1, day, hour, minute, second));
  if (calendarDate.getUTCFullYear() !== year || calendarDate.getUTCMonth() !== month - 1 ||
      calendarDate.getUTCDate() !== day || calendarDate.getUTCHours() !== hour ||
      calendarDate.getUTCMinutes() !== minute || calendarDate.getUTCSeconds() !== second) return null;

  let milliseconds: number;
  if (utcOffset) {
    const offset = utcOffset.trim().match(/^([+-])(\d{2}):(\d{2})$/);
    if (!offset || Number(offset[2]) > 23 || Number(offset[3]) > 59) return null;
    const minutes = (Number(offset[2]) * 60 + Number(offset[3])) * (offset[1] === '-' ? -1 : 1);
    milliseconds = calendarDate.getTime() - minutes * 60_000;
  } else {
    milliseconds = new Date(year, month - 1, day, hour, minute, second).getTime();
  }
  return milliseconds >= Date.UTC(2020, 0, 1) && milliseconds < Date.UTC(2100, 0, 1)
    ? Math.trunc(milliseconds) : null;
}

function readRationalTriplet(
  view: DataView,
  tiffStart: number,
  segmentEnd: number,
  entry: IfdEntry | undefined,
  littleEndian: boolean
) {
  if (!entry || entry.type !== 5 || entry.count < 3) return null;
  const offset = tiffStart + entry.valueOffset;
  if (offset < tiffStart || offset + 24 > segmentEnd) return null;
  const values = [0, 1, 2].map((index) => {
    const valueOffset = offset + index * 8;
    const numerator = view.getUint32(valueOffset, littleEndian);
    const denominator = view.getUint32(valueOffset + 4, littleEndian);
    return denominator === 0 ? null : numerator / denominator;
  });
  if (values.some((value) => value === null)) return null;
  const [degrees, minutes, seconds] = values as number[];
  if (minutes >= 60 || seconds >= 60) return null;
  return degrees + minutes / 60 + seconds / 3600;
}

async function fileToJpegBlob(file: File): Promise<Blob> {
  if (file.type === 'image/jpeg' && file.size <= MAX_IMAGE_BYTES) {
    return file;
  }

  const bitmap = await createImageBitmap(file);
  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not prepare image for upload.');
  ctx.drawImage(bitmap, 0, 0);
  bitmap.close();

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Could not convert image to JPEG.'));
      },
      'image/jpeg',
      0.95
    );
  });
}

async function sha256Hex(blob: Blob): Promise<string> {
  const buffer = await blob.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}
